import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "d-flex flex-column align-center px-6 py-8" }
const _hoisted_2 = {
  key: 0,
  class: "max-w-xl w-full"
}
const _hoisted_3 = {
  key: 1,
  class: "d-flex align-center pa-5 rounded-lg bg-grey-lighten-4 max-w-xl w-full shadow-sm"
}
const _hoisted_4 = {
  key: 2,
  class: "max-w-xl w-full",
  "data-cy": "payabli-status-card"
}
const _hoisted_5 = {
  key: 0,
  class: "d-flex align-center pa-5 rounded-lg bg-grey-lighten-4 shadow-sm",
  "data-cy": "payabli-not-connected"
}
const _hoisted_6 = {
  key: 1,
  "data-cy": "payabli-connected"
}
const _hoisted_7 = { class: "d-flex align-center pa-5 rounded-lg bg-green-lighten-5 shadow-sm" }
const _hoisted_8 = { class: "d-flex align-center flex-wrap" }
const _hoisted_9 = { class: "text-body-2 text-grey-darken-2 mt-1" }
const _hoisted_10 = {
  key: 0,
  class: "d-flex align-center text-body-2 text-grey-darken-1 mt-3 ml-1"
}
const _hoisted_11 = { class: "d-flex align-center mb-2" }

import { reactive, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { usePayabliIntegrationStore, PayabliStatus } from './stores/usePayabliIntegrationStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlSelect from '@/components/LlSelect.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';
import PayabliLogo from '@/components/SVG/PayabliLogo.vue';
import { pluralize, getShortDateString } from '@/services/utils';
import LlIcon from '@/components/LlIcon.vue';

interface ExpenseAccount {
  id: string;
  label: string;
}

interface AccountingService {
  accounts: () => Promise<Record<string, unknown[]>>;
  combineQuickBooksAccounts: (
    accounts: Record<string, unknown[]>,
    types: string[],
  ) => ExpenseAccount[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PayabliIntegration',
  props: {
    accountingService: {}
  },
  setup(__props: any) {

const props = __props;

const { handleError, handleSuccess } = useSnackbarHandler();
const meStore = useMeStore();
const { features, company } = storeToRefs(meStore);
const payabliStore = usePayabliIntegrationStore();

const payabliState = reactive({
  loading: true,
  saving: false,
  status: payabliStore.status as PayabliStatus,
  expenseAccounts: [] as ExpenseAccount[],
  feeExpenseAccountId: null as string | null,
});

const formattedConnectedSince = computed(() => {
  if (!payabliState.status.connectedSince) return '';
  return getShortDateString(payabliState.status.connectedSince);
});

onMounted(async () => {
  payabliState.loading = true;
  try {
    await meStore.getMe();

    if (features.value?.paayabli) {
      payabliState.status = await payabliStore.fetchStatus(company.value.id);

      if (payabliState.status.connected) {
        payabliState.feeExpenseAccountId = payabliState.status.feeExpenseAccountId;
        const accounts = await props.accountingService.accounts();
        payabliState.expenseAccounts = props.accountingService.combineQuickBooksAccounts(
          accounts,
          ['expense'],
        );
      }
    }
  } catch (error) {
    handleError('Failed to load Payabli settings');
  } finally {
    payabliState.loading = false;
  }
});

async function onFeeAccountChange(feeExpenseAccountId: string) {
  payabliState.saving = true;
  try {
    payabliState.status = await payabliStore.updateFeeAccount(
      company.value.id,
      feeExpenseAccountId,
    );
    handleSuccess('Payabli fee category updated');
  } catch (error) {
    payabliState.feeExpenseAccountId = payabliState.status.feeExpenseAccountId;
  } finally {
    payabliState.saving = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_divider = _resolveComponent("v-divider")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PayabliLogo, {
      "aria-label": "Payabli logo",
      class: "mb-4"
    }),
    _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-body-2 text-grey-darken-1 text-center mb-6 max-w-xl" }, " Send client settlement disbursements through Payabli. ", -1)),
    (payabliState.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_v_skeleton_loader, { type: "list-item-two-line, divider, list-item" })
        ]))
      : (!_unref(features).paayabli)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_v_avatar, {
              color: "blue-grey-darken-1",
              size: "44",
              class: "mr-4 flex-shrink-0"
            }, {
              default: _withCtx(() => [
                _createVNode(LlIcon, {
                  icon: "mdi-information-outline",
                  color: "white",
                  size: "22"
                })
              ]),
              _: 1
            }),
            _cache[1] || (_cache[1] = _createElementVNode("div", { class: "text-body-2 text-grey-darken-2" }, " Payabli e-disbursements is not available for your account. Please contact LeanLaw support for more information. ", -1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (!payabliState.status.connected)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_component_v_avatar, {
                    color: "blue-grey-darken-1",
                    size: "44",
                    class: "mr-4 flex-shrink-0"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(LlIcon, {
                        icon: "mdi-close",
                        color: "white",
                        size: "22"
                      })
                    ]),
                    _: 1
                  }),
                  _cache[2] || (_cache[2] = _createElementVNode("div", null, [
                    _createElementVNode("div", { class: "text-subtitle-1 font-weight-bold text-grey-darken-3" }, " Not Connected "),
                    _createElementVNode("div", { class: "text-body-2 text-grey-darken-2 mt-1" }, " Contact LeanLaw support to enable e-disbursements. ")
                  ], -1))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_component_v_avatar, {
                      color: "success",
                      size: "44",
                      class: "mr-4 flex-shrink-0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LlIcon, {
                          icon: "mdi-check",
                          color: "white",
                          size: "22"
                        })
                      ]),
                      _: 1
                    }),
                    _createElementVNode("div", null, [
                      _createElementVNode("div", _hoisted_8, [
                        _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-subtitle-1 font-weight-bold text-grey-darken-3 mr-2" }, " Connected ", -1)),
                        _createVNode(LlStatusBadge, null, {
                          default: _withCtx(() => [
                            _createTextVNode("as of " + _toDisplayString(formattedConnectedSince.value), 1)
                          ]),
                          _: 1
                        }),
                        (payabliState.status.sandbox)
                          ? (_openBlock(), _createBlock(LlStatusBadge, {
                              key: 0,
                              tone: "amber",
                              class: "ml-2",
                              "data-cy": "payabli-sandbox-badge"
                            }, {
                              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                _createTextVNode(" Sandbox ", -1)
                              ]))]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_9, _toDisplayString(payabliState.status.defaultPaypointLabel), 1)
                    ])
                  ]),
                  (payabliState.status.paypointCount > 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createVNode(LlIcon, {
                          icon: "mdi-bank-outline",
                          size: "16",
                          color: "grey-darken-1",
                          class: "mr-1"
                        }),
                        _createTextVNode(" " + _toDisplayString(payabliState.status.paypointCount - 1) + " other " + _toDisplayString(_unref(pluralize)(payabliState.status.paypointCount - 1, 'paypoint')) + " connected ", 1)
                      ]))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_divider, { class: "my-6" }),
                  _createElementVNode("div", _hoisted_11, [
                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-body-2 font-weight-medium" }, "Processing-fee category", -1)),
                    _createVNode(LlTooltip, {
                      icon: "help",
                      class: "ml-2"
                    }, {
                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                        _createTextVNode(" The QuickBooks expense account Payabli's processing fee is posted to. ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _createVNode(LlSelect, {
                    "data-cy": "payabli-fee-account",
                    modelValue: payabliState.feeExpenseAccountId,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((payabliState.feeExpenseAccountId) = $event)),
                      onFeeAccountChange
                    ],
                    items: payabliState.expenseAccounts,
                    "item-title": "label",
                    "item-value": "id",
                    "return-object": false,
                    "is-autocomplete": true,
                    "hide-details": true,
                    loading: payabliState.saving,
                    disabled: payabliState.saving,
                    placeholder: "Payabli Processing Fees",
                    class: "max-w-sm"
                  }, null, 8, ["modelValue", "items", "loading", "disabled"]),
                  _cache[7] || (_cache[7] = _createElementVNode("div", { class: "text-caption text-grey-darken-1 mt-1" }, " Default — change to post fees to a different QuickBooks expense category. ", -1))
                ]))
          ]))
  ]))
}
}

})